import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { FlagFrance, FlagRussia, FlagSerbia, FlagUnitedKingdom } from './FlagLanguageIcons';
import type { AppLocale } from '../i18n/messages';
import { LOCALE_LABELS } from '../i18n/messages';
import { controlPill } from '../theme/controlMetrics';

const LANG_ORDER: AppLocale[] = ['sr', 'ru', 'en', 'fr'];

const FLAG_W = 26;
const FLAG_H = Math.round((FLAG_W * 2) / 3);

function LangFlag({ code }: { code: AppLocale }) {
  const p = { width: FLAG_W, height: FLAG_H };
  if (code === 'sr') return <FlagSerbia {...p} />;
  if (code === 'ru') return <FlagRussia {...p} />;
  if (code === 'fr') return <FlagFrance {...p} />;
  return <FlagUnitedKingdom {...p} />;
}

/**
 * Цвета выбранной/невыбранной ячейки. Раньше они были захардкожены прямо в стилях
 * (`#93c5fd` поверх `rgba(37,99,235,0.2)`) — ряд флагов стоял в окне настроек сразу под
 * чипами тем и подсвечивался мимо палитры, в любой теме одинаково синим. Форму пропа
 * повторяем за `ThemePickerRow`, чтобы у двух соседних рядов был один способ окраски.
 */
type LangPalette = {
  chipBg: string;
  chipBorder: string;
  chipSelectedBg: string;
  chipSelectedBorder: string;
};

type Props = {
  locale: AppLocale;
  onSelect: (l: AppLocale) => void;
  palette: LangPalette;
};

/** Compact flag row: Sr / Ru / En / Fr — same behavior as on the login screen. */
export function LanguagePickerRow({ locale, onSelect, palette }: Props) {
  return (
    <View style={styles.langRow}>
      {LANG_ORDER.map((code) => {
        const sel = locale === code;
        const { a11yLabel } = LOCALE_LABELS[code];
        return (
          <Pressable
            key={code}
            onPress={() => onSelect(code)}
            style={[
              styles.langHit,
              {
                backgroundColor: sel ? palette.chipSelectedBg : palette.chipBg,
                borderColor: sel ? palette.chipSelectedBorder : palette.chipBorder,
              },
            ]}
            accessibilityLabel={a11yLabel}
            accessibilityRole="radio"
            accessibilityState={{ selected: sel }}
            hitSlop={6}>
            <View style={styles.langFlagClip}>
              <LangFlag code={code} />
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  langRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    gap: 8,
  },
  // Та же пилюля, что у чипов тем этажом выше и у чипов-фильтров списка чатов
  // (`controlMetrics`). Горизонтальные поля свои и узкие: четыре флага на `flex: 1`,
  // поля пилюли (14) на узком телефоне сплющили бы флаг.
  langHit: {
    ...controlPill,
    flex: 1,
    minWidth: 0,
    paddingHorizontal: 4,
  },
  langFlagClip: {
    borderRadius: 3,
    overflow: 'hidden',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(15, 23, 42, 0.35)',
  },
});
